import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../../../Services/axiosCommon";
import Modal from "../../../../Components/Utils/AttachmentView";
import PDFViewer from "../../../../Components/Utils/PDFViewer";
import PRPdfOther from "../../ViewPdfTemplate/PRPdfOther";
import UseDropDownPR from "../../../../Components/Hooks/UserddNewPr";

import PrHeader from "./components/PrHeader";
import PrDetailsCard from "./components/PrDetailsCard";
import PrProductTable from "./components/PrProductTable";
import PrSidebar from "./components/PrSidebar";

function ViewPr() {
  const { prViewId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const pageIndex = location.state?.pageIndex;
  const searchQuery = location.state?.searchQuery;
  const initialPrData = location?.state?.data;

  const [data, setData] = useState(initialPrData || null);
  const [search, setSearch] = useState("");
  const [selectedPrId, setSelectedPrId] = useState(prViewId || "");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showPdfComponent, setShowPdfComponent] = useState(false);

  // Sync state and invalidate query cache when returning from edit page or route changes
  useEffect(() => {
    if (location.state?.data) {
      setData(location.state.data);
    }
    if (prViewId) {
      queryClient.invalidateQueries({ queryKey: ["PR-details", prViewId] });
      queryClient.invalidateQueries({ queryKey: ["PR-products-lists", prViewId] });
    }
  }, [location.state?.data, prViewId, queryClient]);

  // Sync selectedPrId when route parameter changes
  useEffect(() => {
    if (prViewId) {
      setSelectedPrId(prViewId);
    }
  }, [prViewId]);

  // Handle dropdown PR selection change
  const handlePrSelectChange = useCallback(
    (newPrId) => {
      if (!newPrId) return;
      setSelectedPrId(newPrId);
      if (String(newPrId) !== String(prViewId)) {
        navigate(`/procurement/purchase-requests/view/${newPrId}`, {
          replace: true,
        });
      }
    },
    [prViewId, navigate]
  );

  // Fetch PR Dropdown Options
  const { data: PR } = UseDropDownPR(
    `/PR/nav-drop-down?search=${search}`
  );

  const prOptions = useMemo(() => {
    const options = PR ? [...PR] : [];
    if (data?.prId && data?.ref) {
      const exists = options.some(
        (item) => String(item.value) === String(data.prId)
      );
      if (!exists) {
        options.unshift({ value: data.prId, label: data.ref });
      }
    }
    return options;
  }, [PR, data?.prId, data?.ref]);

  const currentIndex = useMemo(
    () =>
      prOptions.findIndex(
        (item) => String(item.value) === String(selectedPrId)
      ),
    [prOptions, selectedPrId]
  );

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < prOptions.length - 1;

  const handlePrevPR = useCallback(() => {
    if (hasPrev) {
      handlePrSelectChange(prOptions[currentIndex - 1].value);
    }
  }, [hasPrev, currentIndex, prOptions, handlePrSelectChange]);

  const handleNextPR = useCallback(() => {
    if (hasNext) {
      handlePrSelectChange(prOptions[currentIndex + 1].value);
    }
  }, [hasNext, currentIndex, prOptions, handlePrSelectChange]);

  // Fetch PR Details using React Query
  const { data: fetchedPrData } = useQuery({
    queryKey: ["PR-details", prViewId],
    queryFn: async () => {
      const res = await axiosInstance.get(`/PR/${prViewId}`);
      return res.data?.data || null;
    },
    enabled: !!prViewId,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (fetchedPrData) {
      setData(fetchedPrData);
    }
  }, [fetchedPrData]);

  // Memoized Attachment List derived from data
  const filePathList = useMemo(() => {
    if (
      data?.attachments &&
      data?.attachments !== "None" &&
      data?.attachments !== "[]"
    ) {
      return Array.isArray(data.attachments) ? data.attachments : [];
    }
    return [];
  }, [data?.attachments]);

  // Fetch PR Product Lists using React Query
  const { data: revData = [], isLoading: isProductsLoading } = useQuery({
    queryKey: ["PR-products-lists", prViewId],
    queryFn: async () => {
      const res = await axiosInstance.get(`/PR/products-lists/${prViewId}`);
      return res.data?.data || [];
    },
    enabled: !!prViewId,
    staleTime: 5 * 60 * 1000,
  });

  // PDF Download Mutation using React Query
  const downloadPdfMutation = useMutation({
    mutationFn: async () => {
      const response = await axiosInstance.get(
        `/PR/download-pdf/${prViewId}`,
        { responseType: "blob" }
      );
      return response.data;
    },
    onSuccess: (blobData) => {
      const pdfBlob = new Blob([blobData], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(pdfBlob);
      link.download = `${data?.ref || "PR"}.pdf`;
      document.body.appendChild(link);
      link.click();
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
    },
    onError: (error) => {
      console.error("Error downloading the PDF:", error);
    },
  });

  const downloadPDF = useCallback(() => {
    downloadPdfMutation.mutate();
  }, [downloadPdfMutation]);

  // Fetch Related PDF Docs using React Query
  const { data: relatedDoc = [] } = useQuery({
    queryKey: ["PR-related-docs", prViewId],
    queryFn: async () => {
      const res = await axiosInstance.get(`/PR/related-docs/${prViewId}`);
      return res.data?.data || [];
    },
    enabled: !!prViewId,
  });

  // Fetch Related Excel Docs using React Query
  const { data: relatedExcelDoc = [] } = useQuery({
    queryKey: ["PR-related-docs-excel", prViewId],
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/PR/related-docs-excel/${prViewId}`
      );
      return res.data?.data || [];
    },
    enabled: !!prViewId,
  });

  return (
    <div className="w-full h-full py-2 px-2 sm:px-4 mb-3 relative">
      {/* Top Header & Action Controls */}
      <PrHeader
        pageIndex={pageIndex}
        searchQuery={searchQuery}
        prOptions={prOptions}
        selectedPrId={selectedPrId}
        onPrSelectChange={handlePrSelectChange}
        handlePrevPR={handlePrevPR}
        handleNextPR={handleNextPR}
        hasPrev={hasPrev}
        hasNext={hasNext}
        setSearch={setSearch}
        downloadPDF={downloadPDF}
        prViewId={prViewId}
        data={data}
      />

      {/* Main Content Area */}
      <div className="flex flex-col lg:flex-row gap-4 w-full lg:h-[calc(100vh-140px)] overflow-y-auto pb-4 text-[#111537]">
        {/* Left 70%: Details Card & Product Table */}
        <div className="w-full lg:w-[70%] bg-white rounded-xl shadow-xs border border-slate-200/80 py-3 px-2 flex flex-col overflow-hidden">
          <div className="h-full overflow-y-auto lg:p-2 lg:pb-8">
            <PrDetailsCard data={data} filePathList={filePathList} />
            <PrProductTable revData={revData} isLoading={isProductsLoading} />
          </div>
        </div>

        {/* Right 30%: Sidebar (Approval, Customer Info, Documents, Comments) */}
        <PrSidebar
          prViewId={prViewId}
          data={data}
          setData={setData}
          relatedDoc={relatedDoc}
          relatedExcelDoc={relatedExcelDoc}
        />

        {/* Image / Attachment Modal */}
        <Modal show={isModalOpen} onClose={() => setIsModalOpen(false)}>
          {selectedImage?.file?.split(".").pop() === "pdf" ? (
            <PDFViewer pdfFile={selectedImage?.file} />
          ) : (
            <img src={selectedImage?.file} alt={selectedImage?.file || "Attachment"} />
          )}
        </Modal>

        {/* Full PDF View Modal */}
        {showPdfComponent && (
          <div className="fixed top-0 left-0 w-full h-full bg-gray-800 bg-opacity-50 z-[9999] flex justify-center items-center">
            <div className="animate-scale-in relative w-[95%] h-[96%] bg-white py-2 px-4 rounded-md shadow-lg overflow-y-auto">
              <button
                className="absolute top-2 right-2 text-white bg-red-600 p-1 rounded"
                onClick={() => setShowPdfComponent(false)}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <PRPdfOther data={data} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ViewPr;
